import { ArrowUp, Mail, MapPin, Phone, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import logo from "../../../assets/images/Logo.png";

// TikTok Inline SVG Icon
// aria-hidden because every render site wraps it in a parent that already
// provides the accessible name (e.g. the social-list <span role="img" aria-label="TikTok">).
const TiktokIcon = ({ width = 18, height = 18, className = "" }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    fill="currentColor"
    className={className}
  >
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const aboutLinks = [
  { label: "Về chúng tôi", href: "/about" },
  { label: "Liên hệ tư vấn", href: "/contact" },
  { label: "Kho giao diện", href: "/store" },
  { label: "Câu hỏi thường gặp", href: "/#contact-section" },
];

const serviceLinks = [
  { label: "Thiết kế Website" },
  { label: "App Mobile" },
  { label: "CRM / ERP tinh gọn" },
  { label: "Digital Marketing" },
  { label: "Tư vấn công nghệ" },
  { label: "Bảo mật hệ thống" },
];

const policyLinks = [
  { label: "Chính sách bảo mật" },
  { label: "Điều khoản dịch vụ" },
  { label: "Quy trình làm việc" },
  { label: "Hỗ trợ sau bàn giao" },
];

const socialLinks = [
  { icon: Facebook, label: "Facebook" },
  { icon: Instagram, label: "Instagram" },
  { icon: Linkedin, label: "LinkedIn" },
  { icon: TiktokIcon, label: "TikTok" },
  { icon: Youtube, label: "YouTube" },
];

const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="mt-20 px-4 pb-4 sm:px-5 sm:pb-5 lg:px-6">
      <div className="overflow-hidden rounded-[28px] border border-red-100 bg-[linear-gradient(180deg,#fffbfb_0%,#fff4f4_42%,#ffeaea_100%)] shadow-[0_24px_70px_rgba(127,29,29,0.08)]">
        <div className="mx-auto w-full max-w-[1280px] px-5 py-9 sm:px-7 sm:py-10 lg:px-10 lg:py-12 xl:px-12">
          <div className="grid gap-8 border-b border-red-100 pb-8 text-left lg:grid-cols-[1.2fr_1fr_1fr] lg:gap-10">
            <div className="flex flex-col items-start gap-2.5 text-left">
              <div className="flex items-center gap-3.5">
                <img
                  src={logo}
                  alt="TrioCore Solutions"
                  className="h-[46px] w-[46px] rounded-full object-cover xl:h-[52px] xl:w-[52px]"
                  width="46"
                  height="46"
                  loading="lazy"
                />
                <div className="flex min-w-0 flex-col leading-none">
                  <div className="flex items-end gap-0.5 text-[18px] sm:text-[19px] xl:text-[23px] font-bold">
                    <span className="text-[#343434]">Trio</span>
                    <span className="text-[#2ad4e8]">Core</span>
                    <span className="ml-1.25 text-[#343434]">Solutions</span>
                  </div>
                  <span className="mt-1 text-[9px] sm:text-[10px] xl:text-[11px] font-medium tracking-[0.1em] text-[#666666]">
                    People. Process. Progress.
                  </span>
                </div>
              </div>
              <p className="w-full max-w-[390px] text-left text-sm leading-[1.7] text-slate-600 sm:text-[15px]">
                TrioCore Solutions đồng hành cùng doanh nghiệp xây dựng nền tảng công nghệ đủ chắc,
                tối ưu vận hành và mở rộng tăng trưởng bằng các giải pháp số rõ mục tiêu, thực tế
                và bền vững.
              </p>
            </div>

            <div className="space-y-4 text-left">
              <h3 className="text-left text-[24px] font-semibold leading-tight text-red-400 sm:text-[26px]">
                Cơ cấu tổ chức
              </h3>
              <div className="space-y-2.5 text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                <p>
                  <span className="font-semibold text-[#2f1a1a]">Kinh doanh:</span>{" "}
                  <span className="text-red-300">0822041909</span>
                </p>
                <p>
                  <span className="font-semibold text-[#2f1a1a]">Hỗ trợ kỹ thuật:</span>{" "}
                  <span className="text-red-300">0822041909</span>
                </p>
                <p>
                  <span className="font-semibold text-[#2f1a1a]">Email:</span>{" "}
                  <span className="text-red-300">info@triocoresolutions.com</span>
                </p>
              </div>
            </div>

            <div className="space-y-4 text-left">
              <h3 className="text-left text-[24px] font-semibold leading-tight text-red-400 sm:text-[26px]">
                Địa chỉ
              </h3>
              <div className="space-y-3.5 text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                <div>
                  <p className="font-semibold text-[#2f1a1a]">Trụ sở</p>
                  <p className="text-red-300">64/9 Nguyễn Phúc Chu, Tân Sơn. HCM</p>
                </div>
                <div>
                  <p className="font-semibold text-[#2f1a1a]">Văn phòng</p>
                  <p className="text-red-300">64/9 Nguyễn Phúc Chu, Tân Sơn. HCM</p>
                </div>
                <div>
                  <p className="font-semibold text-[#2f1a1a]">Website</p>
                  <p className="text-red-300">www.triocoresolutions.com.vn</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-8 border-b border-red-100 py-8 text-left sm:py-10 lg:grid-cols-[1fr_1fr_1fr_0.95fr]">
            <div className="space-y-3.5 text-left">
              <h4 className="text-left text-[22px] font-semibold text-red-400 sm:text-[24px]">
                Về chúng tôi
              </h4>
              <ul className="space-y-2.5 text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                {aboutLinks.map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="transition hover:text-red-300">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3.5 text-left">
              <h4 className="text-left text-[22px] font-semibold text-red-400 sm:text-[24px]">
                Dịch vụ của chúng tôi
              </h4>
              <ul className="space-y-2.5 text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                {serviceLinks.map((item) => (
                  <li key={item.label}>
                    <span className="cursor-default transition">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3.5 text-left">
              <h4 className="text-left text-[22px] font-semibold text-red-400 sm:text-[24px]">
                Quy định - Điều khoản
              </h4>
              <ul className="space-y-2.5 text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                {policyLinks.map((item) => (
                  <li key={item.label}>
                    <span className="cursor-default transition">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4 text-left">
              <h4 className="text-left text-[22px] font-semibold text-red-400 sm:text-[24px]">
                Liên hệ
              </h4>
              <div className="space-y-4 text-left">
                <div className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-full border border-red-200 bg-white text-red-400">
                    <Phone size={20} />
                  </div>
                  <div className="text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                    <p className="font-semibold text-[#2f1a1a]">Điện thoại</p>
                    <a href="tel:0822041909" className="text-red-300 transition hover:text-red-200">
                      0822041909
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-full border border-red-200 bg-white text-red-400">
                    <Mail size={20} />
                  </div>
                  <div className="text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                    <p className="font-semibold text-[#2f1a1a]">Email</p>
                    <a
                      href="mailto:info@triocoresolutions.com"
                      className="break-all text-red-300 transition hover:text-red-200"
                    >
                      info@triocoresolutions.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="mt-0.5 flex h-11 w-11 items-center justify-center rounded-full border border-red-200 bg-white text-red-400">
                    <MapPin size={20} />
                  </div>
                  <div className="text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
                    <p className="font-semibold text-[#2f1a1a]">Website</p>
                    <a
                      href="https://triocoresolutions.com.vn/"
                      className="text-red-300 transition hover:text-red-200"
                    >
                      www.triocoresolutions.com.vn
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-start gap-4 pt-6 text-left sm:pt-8 lg:flex-row lg:items-center lg:justify-between">
            <p className="text-left text-sm leading-6 text-slate-600 sm:text-[15px]">
              Copyright ©2026 <span className="text-red-300">TrioCore Solutions</span>. All Rights
              Reserved.
            </p>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              {socialLinks.map((item) => {
                const IconComponent = item.icon;
                return (
                  <span
                    key={item.label}
                    role="img"
                    aria-label={item.label}
                    className="flex h-11 w-11 cursor-default items-center justify-center rounded-full border border-red-100 bg-white text-[#2f1a1a] opacity-85"
                  >
                    <IconComponent width={18} height={18} aria-hidden="true" focusable={false} />
                  </span>
                );
              })}

              <button
                onClick={scrollToTop}
                className="ml-1 flex h-11 w-11 items-center justify-center rounded-full border border-red-100 bg-white text-[#2f1a1a] transition hover:-translate-y-0.5 hover:border-red-300 hover:text-red-400"
                aria-label="Lên đầu trang"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
